import React, { useState } from 'react'
import { IconChevronDown } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { MoreActionsIcon } from '@/components/icons/MoreActionsIcon'

import './ActionsDropdown.scss'

/**
 * @import import { ActionsDropdown } from '@/components/dropdowns/ActionsDropdown'
 * @param {object} props
 * @param {string} [props.triggerLabel] Text on the trigger button, followed by a chevron; omitting it renders an icon-only trigger
 * @param {React.ReactNode} [props.icon] Icon for the icon-only trigger, replacing the default more-actions icon; ignored when triggerLabel is set
 * @param {object} [props.buttonProps] Props spread onto the trigger Button
 * @param {Array<object>} [props.actions] Menu items with label, onClick, and optional icon, testId, isDanger, disabled, disabledTooltip, hasSeparator
 * @param {string} [props.dataTestId] data-testid on the trigger button
 */
export const ActionsDropdown = ({
  triggerLabel,
  icon,
  buttonProps = {},
  actions = [],
  dataTestId,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const trigger = (
    <Button
      active={isOpen}
      small
      square={!triggerLabel}
      data-testid={dataTestId || 'actions-dropdown'}
      {...buttonProps}
    >
      {triggerLabel}
      {triggerLabel ? <IconChevronDown /> : icon || <MoreActionsIcon />}
    </Button>
  )

  return (
    <InteractiveTooltip
      clickable
      open={isOpen}
      setOpen={setIsOpen}
      placement="bottom-end"
      theme="light"
      className="actions-dropdown-popup"
      content={
        <div className="actions-dropdown-menu" role="menu">
          {actions.map(
            ({
              label,
              onClick,
              testId,
              isDanger,
              disabled,
              disabledTooltip,
              hasSeparator,
              icon: actionIcon,
            }) => (
              <React.Fragment key={testId || label}>
                {hasSeparator && <div className="actions-dropdown-divider" />}
                <Button
                  basic
                  critical={isDanger}
                  className="dropdown-action-button"
                  onClick={(event) => {
                    event.stopPropagation()

                    onClick(event)
                    setIsOpen(false)
                  }}
                  data-testid={testId}
                  role="menuitem"
                  disabled={disabled}
                  disabledTooltip={disabledTooltip}
                >
                  <span className="flex-align-center small-gap">
                    {actionIcon && <span className="dropdown-action-icon">{actionIcon}</span>}
                    {label}
                  </span>
                </Button>
              </React.Fragment>
            )
          )}
        </div>
      }
    >
      {trigger}
    </InteractiveTooltip>
  )
}
